import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IActivity, KeyStage, TimelineSequenceConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { Clock, RotateCcw } from 'lucide-react-native';

interface TimelineSequenceActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const TimelineSequenceActivity: React.FC<TimelineSequenceActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as TimelineSequenceConfig;
    const allEvents = useMemo(() => config.events || [], [config.events]);
    const eras = useMemo(() => config.eras || [], [config.eras]);

    // Ordered array of placed event IDs
    const [placedEventIds, setPlacedEventIds] = useState<string[]>([]);
    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [statusMessage, setStatusMessage] = useState<string>(
        config.prompt || 'Place the historical events in chronological order from earliest to latest.'
    );
    const [hasCompleted, setHasCompleted] = useState(false);

    const unplacedEvents = useMemo(() => {
        return allEvents.filter((e) => !placedEventIds.includes(e.id));
    }, [allEvents, placedEventIds]);

    const handlePlaceEvent = (id: string) => {
        if (hasCompleted) return;
        setPlacedEventIds((prev) => [...prev, id]);
        setTrafficStatus('idle');
    };

    const handleRemoveEvent = (id: string) => {
        if (hasCompleted) return;
        setPlacedEventIds((prev) => prev.filter((item) => item !== id));
        setTrafficStatus('idle');
    };

    const handleReset = () => {
        if (hasCompleted) return;
        setPlacedEventIds([]);
        setTrafficStatus('idle');
        setStatusMessage('Tap events in the correct chronological sequence.');
    };

    const handleCheck = () => {
        if (placedEventIds.length < allEvents.length || hasCompleted) return;

        // Check if placed in ascending orderIndex
        let isCorrect = true;
        for (let i = 0; i < placedEventIds.length; i++) {
            const ev = allEvents.find((e) => e.id === placedEventIds[i]);
            if (!ev || ev.orderIndex !== i) {
                isCorrect = false;
                break;
            }
        }

        if (isCorrect) {
            setTrafficStatus('correct');
            setStatusMessage('Chronology Master! All historical events are placed in accurate historical order.');
            setHasCompleted(true);

            useStrokeStore.getState().addCustomAnswer({
                activityId: activity.id,
                value: placedEventIds,
                mechanic: 'timeline_sequence',
                isCorrect: true,
                score: 100,
            });

            setTimeout(() => {
                onComplete();
            }, 1200);
        } else {
            setTrafficStatus('incorrect');
            setStatusMessage('Check the timeline: some earlier events are placed after later events.');
        }
    };

    return (
        <ScrollView
            style={styles.scrollWrapper}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
        >
            <StageCard testID="timeline-sequence-card">
                <View style={styles.container}>
                {/* Header Row */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badge}>
                            <Clock size={14} color="#4F46E5" />
                            <Text style={styles.badgeText}>Chronological Timeline Sequencer</Text>
                        </View>
                        <Text style={styles.promptText}>{config.prompt || 'Arrange events chronologically.'}</Text>
                    </View>
                    <View style={styles.headerControls}>
                        <View style={styles.counterPill}>
                            <Text style={styles.counterText}>
                                {placedEventIds.length} / {allEvents.length} Placed
                            </Text>
                        </View>
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Eras Legend Bar */}
                {eras.length > 0 && (
                    <View style={styles.erasLegendRow}>
                        {eras.map((era) => (
                            <View key={era.id} style={[styles.eraPill, { backgroundColor: era.color || '#64748B' }]}>
                                <Text style={styles.eraPillText}>{era.label}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* Timeline Track */}
                <View style={styles.timelineSection}>
                    <View style={styles.timelineHeader}>
                        <Text style={styles.timelineTitle}>HISTORICAL TIMELINE (PAST ➔ PRESENT):</Text>
                        <TouchableOpacity
                            style={styles.resetButton}
                            onPress={handleReset}
                            disabled={hasCompleted || placedEventIds.length === 0}
                            accessibilityLabel="Reset timeline"
                            testID="timeline-reset-button"
                        >
                            <RotateCcw size={14} color="#64748B" />
                            <Text style={styles.resetButtonText}>Reset</Text>
                        </TouchableOpacity>
                    </View>

                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={true}
                        style={styles.timelineScroll}
                        contentContainerStyle={styles.timelineScrollContent}
                    >
                        {allEvents.map((_, slotIdx) => {
                            const eventId = placedEventIds[slotIdx];
                            const event = eventId ? allEvents.find((e) => e.id === eventId) : null;

                            return (
                                <View key={`slot_${slotIdx}`} style={styles.slotContainer}>
                                    <View style={styles.slotRulerMarker}>
                                        <Text style={styles.slotNumber}>{slotIdx + 1}</Text>
                                    </View>
                                    {event ? (
                                        <TouchableOpacity
                                            style={styles.placedCard}
                                            onPress={() => handleRemoveEvent(event.id)}
                                            disabled={hasCompleted}
                                            accessibilityLabel={`Remove ${event.title}`}
                                            testID={`placed-event-${event.id}`}
                                        >
                                            <View style={styles.placedCardHeader}>
                                                <Text style={styles.eventYearLabel}>{event.yearLabel}</Text>
                                            </View>
                                            <Text style={styles.eventTitle}>{event.title}</Text>
                                            {event.summary && (
                                                <Text style={styles.eventSummary} numberOfLines={2}>
                                                    {event.summary}
                                                </Text>
                                            )}
                                        </TouchableOpacity>
                                    ) : (
                                        <View style={styles.emptySlot}>
                                            <Text style={styles.emptySlotText}>Drop event #{slotIdx + 1}</Text>
                                        </View>
                                    )}
                                </View>
                            );
                        })}
                    </ScrollView>
                </View>

                {/* Status Bar */}
                <View style={styles.statusBar}>
                    <Text style={styles.statusText}>{statusMessage}</Text>
                </View>

                {/* Available Event Cards Pool */}
                <View style={styles.poolSection}>
                    <Text style={styles.poolTitle}>Historical Events Pool:</Text>
                    <View style={styles.poolGrid}>
                        {unplacedEvents.length === 0 ? (
                            <Text style={styles.allPlacedText}>All events positioned on the timeline.</Text>
                        ) : (
                            unplacedEvents.map((ev) => (
                                <TouchableOpacity
                                    key={ev.id}
                                    style={styles.unplacedCard}
                                    onPress={() => handlePlaceEvent(ev.id)}
                                    disabled={hasCompleted}
                                    accessibilityLabel={`Place ${ev.title}`}
                                    testID={`pool-event-${ev.id}`}
                                >
                                    <View style={styles.unplacedCardHeader}>
                                        <Text style={styles.unplacedYear}>{ev.yearLabel}</Text>
                                        {ev.icon && <Text style={styles.unplacedIcon}>{ev.icon}</Text>}
                                    </View>
                                    <Text style={styles.unplacedTitle}>{ev.title}</Text>
                                </TouchableOpacity>
                            ))
                        )}
                    </View>

                    <View style={styles.actionRow}>
                        <JuicyButton
                            label="CHECK CHRONOLOGY"
                            variant="primary"
                            size="medium"
                            onPress={handleCheck}
                            disabled={hasCompleted || placedEventIds.length < allEvents.length}
                            testID="timeline-check-button"
                        />
                    </View>
                </View>
            </View>
        </StageCard>
    </ScrollView>
    );
};

const styles = StyleSheet.create({
    scrollWrapper: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    scrollContent: {
        padding: 12,
        alignItems: 'center',
    },
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    promptText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    counterPill: {
        backgroundColor: '#1E293B',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
    },
    counterText: {
        fontSize: 13,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    erasLegendRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    eraPill: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    eraPillText: {
        fontSize: 11,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    timelineSection: {
        backgroundColor: '#F8FAFC',
        padding: 12,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 8,
    },
    timelineHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    timelineTitle: {
        fontSize: 11,
        fontWeight: '800',
        color: '#64748B',
    },
    resetButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 6,
    },
    resetButtonText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
    },
    timelineScroll: {
        minHeight: 120,
    },
    timelineScrollContent: {
        flexDirection: 'row',
        gap: 12,
        paddingVertical: 4,
    },
    slotContainer: {
        width: 140,
        alignItems: 'center',
        gap: 6,
    },
    slotRulerMarker: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: '#E2E8F0',
        justifyContent: 'center',
        alignItems: 'center',
    },
    slotNumber: {
        fontSize: 12,
        fontWeight: '800',
        color: '#475569',
    },
    emptySlot: {
        width: 130,
        height: 84,
        borderRadius: 10,
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: '#CBD5E1',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 8,
    },
    emptySlotText: {
        fontSize: 12,
        fontWeight: '600',
        color: '#94A3B8',
        textAlign: 'center',
    },
    placedCard: {
        width: 130,
        height: 84,
        borderRadius: 10,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#94A3B8',
        padding: 8,
        justifyContent: 'space-between',
        elevation: 2,
    },
    placedCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    eventYearLabel: {
        fontSize: 11,
        fontWeight: '900',
        color: '#2563EB',
    },
    eventTitle: {
        fontSize: 12,
        fontWeight: '800',
        color: '#0F172A',
        lineHeight: 15,
    },
    eventSummary: {
        fontSize: 10,
        color: '#64748B',
    },
    statusBar: {
        backgroundColor: '#F1F5F9',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
    statusText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        textAlign: 'center',
    },
    poolSection: {
        gap: 8,
        alignItems: 'center',
    },
    poolTitle: {
        fontSize: 13,
        fontWeight: '800',
        color: '#475569',
        alignSelf: 'flex-start',
    },
    poolGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        width: '100%',
    },
    allPlacedText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#10B981',
        paddingVertical: 6,
    },
    unplacedCard: {
        flex: 1,
        minWidth: 140,
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        padding: 10,
        gap: 4,
    },
    unplacedCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    unplacedYear: {
        fontSize: 12,
        fontWeight: '900',
        color: '#2563EB',
    },
    unplacedIcon: {
        fontSize: 16,
    },
    unplacedTitle: {
        fontSize: 13,
        fontWeight: '800',
        color: '#1E293B',
    },
    actionRow: {
        marginTop: 6,
    },
});
